// index.tsx
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React from 'react'
import {
  createTheme, LinearProgress, Theme, ThemeProvider, Typography
} from '@mui/material'
import Box from '@mui/material/Box'
import { DarkVoyixPurple, LightVoyixPurple, RedError } from '../../common/constants/colorConstants'

declare module '@mui/material/styles' {
  interface Palette {
    customProgressColor: Palette['primary'];
  }

  interface PaletteOptions {
    customProgressColor?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/LinearProgress' {
  interface LinearProgressPropsColorOverrides {
    customProgressColor: true
  }
}

function getCustomTheme(color: string): Theme {
  return createTheme({
    palette: {
      customProgressColor: {
        main: color
      }
    }
  })
}

export interface ProgressBarProps {
  /**
   * The value for progression.
   */
  value: number

  /**
   * Indicates whether the progress bar should render as an error.
   */
  error?: boolean
}

function getBarColor(progress: number): string {
  return progress >= 100 ? DarkVoyixPurple : LightVoyixPurple
}

export function ProgressBar({
  value,
  error
}: Readonly<ProgressBarProps>) {
  const barColor: string = error ? RedError : getBarColor(value)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', marginRight: 1 }}>
        <ThemeProvider theme={getCustomTheme(barColor)}>
          <LinearProgress
            variant="determinate"
            value={value}
            color="customProgressColor"
          />
        </ThemeProvider>
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  )
}

ProgressBar.defaultProps = {
  error: false
}
